import { useCallback } from 'react';
import { useLazyQuery } from '@apollo/client';
import { GET_ASSIGNABLE_USERS, GET_ASSIGNABLE_USERS_UPERCUT } from '@gql/jobs';
import { useAuth } from '@contexts/AuthContext';
import { AssigneeType } from '../jobOffer.types';

type PaginationInput = {
    offset?: number;
    limit?: number;
};

type FetchAssignableUsersParams = {
    query?: string;
    pagination?: PaginationInput;
    assigneeTypes?: AssigneeType[];
};

type UseGetAssignableUsersParams = {
    proId?: string;
};

const normalizeParams = (params: FetchAssignableUsersParams, isUpercut: boolean): FetchAssignableUsersParams => {
    if (!isUpercut) {
        return params;
    }

    if (params.assigneeTypes && params.assigneeTypes.length > 0) {
        return params;
    }

    return {
        ...params,
        assigneeTypes: [AssigneeType.Pro, AssigneeType.OrganizationUser],
    };
};

export const useGetAssignableUsers = ({ proId }: UseGetAssignableUsersParams = {}) => {
    const { isUpercut } = useAuth();

    const queryDocument = isUpercut ? GET_ASSIGNABLE_USERS_UPERCUT : GET_ASSIGNABLE_USERS;

    const [fetchAssignableUsers, queryState] = useLazyQuery(queryDocument, {
        fetchPolicy: 'network-only',
    });
    const { fetchMore } = queryState;

    const fetchUsers = useCallback((params: FetchAssignableUsersParams) => {
        if (isUpercut && !proId) {
            return;
        }

        const normalizedParams = normalizeParams(params, isUpercut);

        fetchAssignableUsers({
            variables: {
                ...normalizedParams,
                ...(isUpercut ? { proId } : {}),
            },
        });
    }, [fetchAssignableUsers, isUpercut, proId]);

    const fetchMoreUsers = useCallback((params: FetchAssignableUsersParams) => {
        if (!fetchMore) {
            return;
        }

        const normalizedParams = normalizeParams(params, isUpercut);

        fetchMore({
            variables: {
                ...normalizedParams,
                ...(isUpercut ? { proId } : {}),
            },
            updateQuery: (prev, { fetchMoreResult }) => {
                const key = isUpercut ? 'getAssignableUsersUpercut' : 'getAssignableUsers';

                if (!fetchMoreResult?.[key]) {
                    return prev;
                }

                return {
                    ...prev,
                    [key]: {
                        ...fetchMoreResult[key],
                        docs: [...(prev?.[key]?.docs ?? []), ...(fetchMoreResult?.[key]?.docs ?? [])],
                    },
                };
            },
        });
    }, [fetchMore, isUpercut, proId]);

    const rootField = isUpercut ? 'getAssignableUsersUpercut' : 'getAssignableUsers';

    return {
        fetchUsers,
        fetchMoreUsers,
        data: queryState.data?.[rootField],
        loading: queryState.loading,
    };
};
